import _ from 'lodash';

import {
  OPERATORS,
  AssignmentRuleResponseType,
  AssignmentRulePipelineResponseType,
  AssignmentRuleRequestType,
} from 'utils/api/modules/entities/entities.types';

import { AssignmentRuleType } from './AssignmentRules';

export const MANAGER = 'manager';

export const isAssignmentRuleResponseType = (rule: AssignmentRuleType): rule is AssignmentRuleResponseType =>
  'targetUsers' in rule;

export function areRuleArraysEqual(
  rulesA: AssignmentRuleResponseType[],
  rulesB: AssignmentRuleResponseType[]
): boolean {
  if (rulesA.length !== rulesB.length) return false;

  const sortedA = _.sortBy(rulesA, 'uuid');
  const sortedB = _.sortBy(rulesB, 'uuid');

  for (let i = 0; i < sortedA.length; i += 1) {
    if (!areRulesEqual(sortedA[i], sortedB[i])) return false;
  }

  return true;
}

export function areRulePipelineArraysEqual(
  rulesA: AssignmentRulePipelineResponseType[],
  rulesB: AssignmentRulePipelineResponseType[]
): boolean {
  if (rulesA.length !== rulesB.length) return false;

  const sortedA = _.sortBy(rulesA, 'uuid');
  const sortedB = _.sortBy(rulesB, 'uuid');

  for (let i = 0; i < sortedA.length; i += 1) {
    if (!areRulePipelinesEqual(sortedA[i], sortedB[i])) return false;
  }

  return true;
}

export function areRulesEqual(ruleA: AssignmentRuleResponseType, ruleB: AssignmentRuleResponseType): boolean {
  if (ruleA.name !== ruleB.name) return false;
  if ((ruleA.description || '') !== (ruleB.description || '')) return false;

  const normalizedConditionsA = normalizeConditions(ruleA.conditions);
  const normalizedConditionsB = normalizeConditions(ruleB.conditions);
  if (!_.isEqual(normalizedConditionsA, normalizedConditionsB)) return false;

  const normalizedUsersA = normalizeUsers(ruleA.targetUsers);
  const normalizedUsersB = normalizeUsers(ruleB.targetUsers);
  if (!_.isEqual(normalizedUsersA, normalizedUsersB)) return false;

  const normalizedGroupsA = normalizeGroups(ruleA.targetGroups);
  const normalizedGroupsB = normalizeGroups(ruleB.targetGroups);
  if (!_.isEqual(normalizedGroupsA, normalizedGroupsB)) return false;

  return true;
}

function areRulePipelinesEqual(
  ruleA: AssignmentRulePipelineResponseType,
  ruleB: AssignmentRulePipelineResponseType
): boolean {
  if (ruleA.name !== ruleB.name) return false;
  if ((ruleA.description || '') !== (ruleB.description || '')) return false;

  const normalizedConditionsA = normalizeConditions(ruleA.conditions);
  const normalizedConditionsB = normalizeConditions(ruleB.conditions);
  if (!_.isEqual(normalizedConditionsA, normalizedConditionsB)) return false;

  const normalizedPipelineA = normalizePipeline(ruleA.pipeline);
  const normalizedPipelineB = normalizePipeline(ruleB.pipeline);
  if (!_.isEqual(normalizedPipelineA, normalizedPipelineB)) return false;

  return true;
}

function normalizeConditions(
  conditions: AssignmentRuleType['conditions']
): Array<{ operator: string; field: string; values: string[] }> {
  return _.sortBy(
    conditions.map((cond) => {
      const [operator] = Object.keys(cond);
      const [field, values] = cond[operator];
      return {
        operator,
        field,
        values: _.sortBy((values || []).map((v) => ('uuid' in v ? v.uuid : v.key))),
      };
    }),
    ['operator', 'field']
  );
}

function normalizeUsers(users: AssignmentRuleResponseType['targetUsers']): { uuid: string; name: string }[] {
  return _.sortBy(users, ['uuid', 'name']);
}

function normalizeGroups(groups: AssignmentRuleResponseType['targetGroups']): { uuid: string; name: string }[] {
  return _.sortBy(
    (groups || []).map(({ uuid, name }) => ({ uuid, name })),
    ['uuid', 'name']
  );
}

function normalizePipeline(pipeline: AssignmentRulePipelineResponseType['pipeline']): { uuid: string; name: string } {
  return { uuid: pipeline.uuid, name: pipeline.name };
}

function extractRuleNumber(ruleName: string): number | null {
  if (!ruleName || typeof ruleName !== 'string') {
    return null;
  }

  const parts = ruleName
    .trim()
    .split(' ')
    .filter((part) => part.length > 0);
  if (parts.length < 2 || parts[0].toLowerCase() !== 'rule') {
    return null;
  }

  const numberPart = parts[1];
  const number = Number(numberPart);

  return isNaN(number) ? null : number;
}

export function getNextRuleName(rules: AssignmentRuleResponseType[] | AssignmentRulePipelineResponseType[]): string {
  if (rules.length === 0) {
    return 'Rule 1';
  }

  const hasInvalidRuleNames = rules.some((rule) => extractRuleNumber(rule.name) === null);

  if (hasInvalidRuleNames) {
    return 'Rule';
  }

  const lastRule = rules.reduce((max, rule) => {
    const maxNumber = extractRuleNumber(max.name)!;
    const ruleNumber = extractRuleNumber(rule.name)!;
    return ruleNumber > maxNumber ? rule : max;
  }, rules[0]);
  const lastRuleNumber = extractRuleNumber(lastRule.name)!;

  return `Rule ${lastRuleNumber + 1}`;
}

export function transformValues<T extends AssignmentRuleResponseType[] | AssignmentRulePipelineResponseType[]>(
  values: AssignmentRuleResponseType[] | AssignmentRulePipelineResponseType[]
): T {
  values.forEach((value) => {
    value.conditions.forEach((condition) => {
      const [operator] = Object.keys(condition);

      if (operator === OPERATORS.submission_form) {
        const uuidValues = condition[operator];
        const transformedValues = (uuidValues as string[]).map((key) => ({ key }));

        // Delete the old operator key
        delete condition[operator];

        // Add the new structure with is_any_of
        condition[OPERATORS.is_any_of] = [
          OPERATORS.submission_form,
          transformedValues as unknown as T[number]['conditions'][number]['is_any_of'][1],
        ];
      }
    });

    // if there is targetExpressions in the rule, it means it's type of AssignmentRuleResponseType
    // because AssignmentRulePipelineResponseType doesn't have `targetExpressions`
    if ('targetExpressions' in value && value.targetExpressions) {
      // TypeScript now knows value is AssignmentRuleResponseType
      const ruleValue = value as AssignmentRuleResponseType;
      // Process targetExpressions if needed
      // ruleValue.targetUsers can be safely accessed here

      // targetExpressions will look like this: [["events__reporter", "manager"]]
      // and it should be converted to targetUsers like this: [{ uuid: "manager", name: "Reporters manager" }]
      const targetExpressionsAsUsers =
        ruleValue.targetExpressions?.map((expression) => ({
          uuid: expression[1],
          name: expression[1] === MANAGER ? "Reporter's manager" : expression[1],
        })) || [];
      ruleValue.targetUsers = [...ruleValue.targetUsers, ...targetExpressionsAsUsers];
      ruleValue.targetGroups = ruleValue.targetGroups || [];
    }
  });

  return values as T;
}

// Transforms a single assignment rule from the store format to the API request format.
export function transformRuleForRequest(
  rule: AssignmentRuleResponseType,
  isEdited: boolean = true
): AssignmentRuleRequestType {
  // Extract user UUIDs from targetUsers
  let targetUsers = rule.targetUsers.map((user) => (typeof user === 'string' ? user : user.uuid));

  // Check if MANAGER is in targetUsers and build targetExpression if so
  const targetExpression: [string, string] | [] = targetUsers.find((user) => user === MANAGER)
    ? ['events__reporter', MANAGER]
    : [];

  // Remove MANAGER from targetUsers if it was found
  if (targetExpression.length > 0) {
    targetUsers = targetUsers.filter((user) => user !== MANAGER);
  }

  // Transform conditions to the request format
  const transformedConditions = rule.conditions.map((condition) => {
    const operator = Object.keys(condition)[0] as keyof typeof OPERATORS;
    const [field, values] = condition[operator];

    // Special handling for submission_form operator
    if (field === OPERATORS.submission_form) {
      return {
        [OPERATORS.submission_form]: values.map((value: { uuid?: string; key?: string }) => value.key!),
      };
    }

    // Standard condition transformation
    return {
      [operator]: [field, values.map((value) => ('uuid' in value ? { uuid: value.uuid } : { key: value.key }))],
    };
  });

  const targetGroups = isEdited
    ? (rule.targetGroups || []).map((group) => (typeof group === 'string' ? group : group.uuid))
    : undefined;

  return {
    ...rule,
    targetUsers,
    targetExpressions:
      targetExpression.length > 0 ? [...(rule.targetExpressions || []), targetExpression as [string, string]] : [],
    conditions: transformedConditions as AssignmentRuleRequestType['conditions'],
    targetGroups,
  };
}
